// Opt-in public kiosk protocol. Ordinary embeds keep their historical bridge.
const UUID =
  /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const SOURCE = "unisoft-louah-kiosk";
const sessions = new WeakMap();
function origin(value) {
  try {
    const u = new URL(value);
    return ["http:", "https:"].includes(u.protocol) && u.origin === value
      ? u.origin
      : null;
  } catch {
    return null;
  }
}
function kioskPolicy(
  query,
  render,
  baseUrl,
  origins = ["https://louah.unisoft.world"],
) {
  if (
    render !== "embed" ||
    typeof query?.louahSession !== "string" ||
    !UUID.test(query.louahSession) ||
    !origins.includes(origin(query.louahParent))
  )
    return null;
  try {
    const base = new URL(baseUrl);
    if (
      !["http:", "https:"].includes(base.protocol) ||
      base.username ||
      base.password
    )
      return null;
    return {
      sessionId: query.louahSession,
      parentOrigin: query.louahParent,
      baseUrl: base.href,
    };
  } catch {
    return null;
  }
}
function kioskContext(
  host = typeof window === "undefined" ? undefined : window,
) {
  try {
    if (!host || host.parent === host || host.origin !== "null") return null;
    const u = new URL(host.location.href);
    const sessionId = u.searchParams.get("louahSession"),
      parentOrigin = origin(u.searchParams.get("louahParent"));
    return u.searchParams.get("render") === "embed" &&
      UUID.test(sessionId || "") &&
      parentOrigin
      ? { sessionId, parentOrigin }
      : null;
  } catch {
    return null;
  }
}
function kioskLocation(value, policy, issuedTicket) {
  try {
    const base = new URL(policy.baseUrl),
      target = new URL(value, base);
    if (
      target.username ||
      target.password ||
      !["http:", "https:"].includes(target.protocol)
    )
      return null;
    const exactTicket =
      typeof issuedTicket === "string" && target.href === issuedTicket;
    if (!exactTicket && target.origin !== base.origin) return null;
    const prefix = base.pathname
      .replace(/\/__embed\/?$/, "")
      .replace(/\/$/, "");
    if (!exactTicket && !target.pathname.startsWith(prefix + "/")) return null;
    const tail = target.pathname
      .slice(prefix.length)
      .replace(/^\/__embed(?=\/)/, "");
    if (
      !exactTicket &&
      !/^\/(?:evenement|evenements|events|form|don|campagne)(?:\/|$)/.test(tail)
    )
      return null;
    target.searchParams.set("render", "embed");
    target.searchParams.set("louahSession", policy.sessionId);
    target.searchParams.set("louahParent", policy.parentOrigin);
    return target.href;
  } catch {
    return null;
  }
}
function send(host, context, type, extra = {}) {
  host.parent.postMessage(
    {
      source: SOURCE,
      version: 1,
      sessionId: context.sessionId,
      type,
      ...extra,
    },
    context.parentOrigin,
  );
}
function kioskNavigate(
  value,
  host = typeof window === "undefined" ? undefined : window,
) {
  const context = kioskContext(host),
    policy = host && sessions.get(host);
  if (!context) return false;
  const next = policy && kioskLocation(value, policy.policy, policy.ticketUrl);
  if (next) {
    send(host, context, "navigation");
    host.location.replace(next);
    return true;
  }
  send(host, context, "blocked");
  return false;
}
/** Only the successful submission owner calls this port. The API, not an
 * anchor or query string, supplies the bearer URL on the organisation primary site. */
function kioskAuthorizeTickets(
  value,
  locale,
  host = typeof window === "undefined" ? undefined : window,
) {
  const context = kioskContext(host),
    session = host && sessions.get(host);
  if (!context || !session || session.policy.sessionId !== context.sessionId)
    return null;
  session.ticketUrl = undefined;
  try {
    if (typeof value !== "string" || value.length > 2048) return null;
    const url = new URL(value);
    const loopback = ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
    if (
      (url.protocol !== "https:" && !(url.protocol === "http:" && loopback)) ||
      url.username ||
      url.password ||
      url.search ||
      url.hash ||
      !/\/billets\/[A-Za-z0-9_-]{20,64}$/.test(url.pathname)
    )
      return null;
    if (locale !== undefined && !/^[a-z]{2}$/.test(locale)) return null;
    // API orderUrl is on the main site without a locale prefix. Retain the
    // language of the form rather than redetecting the kiosk browser language.
    if (locale)
      url.pathname = url.pathname.replace(
        /(\/billets\/[A-Za-z0-9_-]{20,64})$/,
        "/" + locale + "$1",
      );
    session.ticketUrl = url.href;
    return url.href;
  } catch {
    return null;
  }
}
function kioskReload(
  host = typeof window === "undefined" ? undefined : window,
) {
  if (!host) return false;
  const context = kioskContext(host);
  if (context) {
    const session = sessions.get(host);
    if (!session || session.policy.sessionId !== context.sessionId)
      return false;
    send(host, context, "navigation");
  }
  host.location.reload();
  return true;
}
function kioskRuntime(
  base,
  host = typeof window === "undefined" ? undefined : window,
) {
  const context = kioskContext(host);
  return context
    ? {
        ...base,
        kiosk: true,
        account: false,
        persistIdentity: false,
        navigate: (value) => kioskNavigate(value, host),
        authorizeTickets: (value, locale) =>
          kioskAuthorizeTickets(value, locale, host),
      }
    : base;
}
function installLouahKiosk(host, policy) {
  const context = kioskContext(host);
  if (
    !context ||
    !policy ||
    context.sessionId !== policy.sessionId ||
    context.parentOrigin !== policy.parentOrigin
  )
    return () => {};
  const session = { policy, ticketUrl: undefined };
  sessions.set(host, session);
  let last = -Infinity;
  const activity = (e) => {
    if (!e.isTrusted) return;
    const now = host.performance.now();
    if (now - last < 400) return;
    last = now;
    send(host, context, "activity");
  };
  const key = (e) => {
    activity(e);
    if (e.isTrusted && e.key === "Escape") {
      e.preventDefault();
      send(host, context, "close");
    }
  };
  const click = (e) => {
    const a = e.target.closest?.("a[href]");
    if (!a) return;
    e.preventDefault();
    e.stopPropagation();
    if (!a.hasAttribute("download")) kioskNavigate(a.href, host);
    else send(host, context, "blocked");
  };
  const submit = (e) => {
    e.preventDefault();
  }; // React may submit anonymously via the API; never a native cookie-bearing form navigation.
  for (const name of ["pointerdown", "input", "wheel", "touchstart"])
    host.document.addEventListener(name, activity, {
      capture: true,
      passive: true,
    });
  host.document.addEventListener("keydown", key, true);
  host.document.addEventListener("click", click, true);
  host.document.addEventListener("submit", submit, true);
  send(host, context, "ready", {
    documentId: host.crypto.randomUUID(),
    documentOrigin: new URL(host.location.href).origin,
  });
  return () => {
    if (sessions.get(host) === session) sessions.delete(host);
    for (const name of ["pointerdown", "input", "wheel", "touchstart"])
      host.document.removeEventListener(name, activity, true);
    host.document.removeEventListener("keydown", key, true);
    host.document.removeEventListener("click", click, true);
    host.document.removeEventListener("submit", submit, true);
  };
}
module.exports = {
  kioskPolicy,
  kioskContext,
  kioskLocation,
  kioskRuntime,
  kioskNavigate,
  kioskAuthorizeTickets,
  kioskReload,
  installLouahKiosk,
};
